import { X } from "@tamagui/lucide-icons";
import { Platform } from "react-native";

import {
  Adapt,
  Button,
  Dialog,
  Heading,
  Link,
  Paragraph,
  Sheet,
  Text,
  XStack,
  YStack,
} from "@/components";
import { shortBrandName } from "@/config/brand";
import { getBrowserInfo } from "@/utils/web";

interface PermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  heading?: string;
}

const OsTypes = {
  Android: "Android",
  IOS: "iOS",
} as const;

const getPermissionSteps = (
  permissionsKey: string,
  browserName: string,
): string[] => {
  const steps: Record<string, string[]> = {
    iosWeb: [
      "Open the Settings app",
      `Tap on ${browserName} > Camera`,
      "Confirm that 'Ask' or 'Allow' is checked",
      `Do the same under ${browserName} > Microphone`,
      `Return to ${browserName}, refresh, and rejoin the call`,
    ],
    androidWeb: [
      `Open ${browserName} settings`,
      "Tap Site settings > Camera",
      "Tap to turn the camera on. If app.curaihealth.com is under Blocked, tap it and choose Allow",
      "Do the same for Microphone",
      "Close settings, refresh, and rejoin the call",
    ],
    web: [
      "Near your address bar should be an icon indicating camera or microphone access has been denied",
      "Click that icon and enable the missing permissions",
      "Refresh this page and rejoin the call",
    ],
    ios: [
      "Open the Settings app",
      `Tap on ${shortBrandName} > Camera`,
      "Confirm that 'Ask' or 'Allow' is checked",
      `Do the same under ${shortBrandName} > Microphone`,
      `Close and reopen ${shortBrandName}`,
    ],
    android: [
      "Open the Settings app",
      `Tap on Apps > ${shortBrandName} > Permissions`,
      "Tap Camera and choose 'Allow'",
      "Do the same for Microphone",
      `Close and reopen ${shortBrandName}`,
    ],
  };

  return steps[permissionsKey] || steps.web;
};

const PermissionsDialog = ({
  open,
  onOpenChange,
  heading = "Permission needed",
}: PermissionsDialogProps) => {
  let permissionsKey: string;
  let browserName = "";

  if (Platform.OS !== "web") {
    permissionsKey = Platform.OS;
  } else {
    const browserInfo = getBrowserInfo();
    const osName = browserInfo?.os?.name;
    browserName = browserInfo?.browser?.name || "your browser";

    if (osName === OsTypes.Android) {
      permissionsKey = "androidWeb";
    } else if (osName === OsTypes.IOS) {
      permissionsKey = "iosWeb";
    } else {
      permissionsKey = "web";
    }
  }

  const steps = getPermissionSteps(permissionsKey, browserName);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Adapt when="maxLg">
        <Sheet
          modal
          open={open}
          onOpenChange={onOpenChange}
          disableDrag
          snapPointsMode="fit"
          zIndex={1000000}
        >
          <Sheet.Frame padding="$200" gap="$150" zIndex={1000001}>
            <Adapt.Contents />
          </Sheet.Frame>
          <Sheet.Overlay
            backgroundColor="$fillOverlay"
            backdropFilter="blur(8px)"
            zIndex={1000000}
          />
        </Sheet>
      </Adapt>
      <Dialog.Portal zIndex={1000000}>
        <Dialog.Overlay
          backgroundColor="$fillOverlay"
          backdropFilter="blur(8px)"
          zIndex={1000000}
        />
        <Dialog.Content
          borderRadius="$150"
          padding="$200"
          minWidth={300}
          maxWidth={400}
          gap="$150"
          zIndex={1000001}
        >
          <XStack justifyContent="space-between" alignItems="center">
            <Heading size="$lg">{heading}</Heading>
            <Dialog.Close asChild>
              <Button.Wrapper
                padding="$50"
                backgroundColor="transparent"
                hoverStyle={{
                  backgroundColor: "$navHover",
                }}
                aria-label="Close"
                dataTrackingId="video-permissions-dialog-close"
              >
                <Text size="$xl" color="$textSecondary" fontWeight="600">
                  <X />
                </Text>
              </Button.Wrapper>
            </Dialog.Close>
          </XStack>

          <Paragraph size="$sm" color="$textSecondary">
            We need permission to access your camera and microphone. Please
            follow the steps below:
          </Paragraph>

          <YStack gap="$100" paddingLeft="$100">
            {steps.map((step, index) => (
              <XStack gap="$100" key={index}>
                <Text size="$sm" color="$textSecondary">
                  •
                </Text>
                <Paragraph size="$sm" color="$textSecondary" flex={1}>
                  {step}
                </Paragraph>
              </XStack>
            ))}
          </YStack>
          <Paragraph size="$sm" color="$textSecondary">
            If you&apos;re still experiencing issues, please reach out to{" "}
            <Link
              size="$sm"
              bold
              color="$textLink"
              href="https://www.curaihealth.com/support"
              external
            >
              support
            </Link>
            .
          </Paragraph>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

export default PermissionsDialog;
